import type {
  CanalDetalhe,
  CanalResumo,
  ContatoInterno,
  DetalhesDaConversa,
  GrupoForm,
  ResultadoBuscaInterna,
  DestinoUpload,
  InscricaoPushEntrada,
  MensagemCanalChat,
  PrazoDeFixacao,
} from '@mymaxx/shared';
import { api } from './api';

/**
 * A conversa interna do lado do técnico de campo.
 *
 * As mesmas rotas do painel, com o token dele: quem está falando o servidor lê do próprio
 * token.
 *
 * Até 21/10 o técnico não começava conversa — nem agenda, nem grupo —, por regra do
 * servidor: a ideia era que a demanda do campo virasse atendimento no setor, que tem
 * protocolo e relatório. Na prática ele só falava com quem o tivesse procurado antes, e
 * ficava esperando alguém puxar assunto. A regra caiu por decisão do PMO; o caminho do
 * atendimento continua existindo para o que precisa de registro.
 */
const base = '/v1/interno';

export const internoApi = {
  canais: () => api.get<CanalResumo[]>(`${base}/canais`),

  /** A agenda de quem pode receber uma conversa nova. Sem dado de contato: só nome. */
  contatos: (busca?: string) =>
    api.get<ContatoInterno[]>(
      `${base}/contatos${busca ? `?busca=${encodeURIComponent(busca)}` : ''}`,
    ),

  /** Abre (ou reencontra) a conversa de duas pessoas. */
  abrirDireto: (com: { usuarioId?: string; clienteId?: string }) =>
    api.post<CanalDetalhe>(`${base}/canais/direto`, com),

  criarGrupo: (form: GrupoForm) => api.post<CanalDetalhe>(`${base}/canais/grupo`, form),

  /** Busca em tudo: mensagem, grupo e pessoa. */
  buscar: (termo: string) =>
    api.get<ResultadoBuscaInterna>(`${base}/busca?termo=${encodeURIComponent(termo)}`),

  /** O painel de detalhes da conversa: mídias, guardadas e grupos em comum. */
  detalhes: (canalId: string) => api.get<DetalhesDaConversa>(`${base}/canais/${canalId}/detalhes`),

  /** Prende a conversa no alto da lista. */
  fixar: (canalId: string, fixado: boolean) =>
    api.post<void>(`${base}/canais/${canalId}/fixar`, { fixado }),

  /** Silencia por um tempo. `null` é "até eu desligar". */
  silenciar: (canalId: string, horas: number | null) =>
    api.post<void>(`${base}/canais/${canalId}/silenciar`, { horas }),
  detalhe: (id: string) => api.get<CanalDetalhe>(`${base}/canais/${id}`),
  escrever: (canalId: string, texto: string, respondeAId?: string) =>
    api.post<MensagemCanalChat>(`${base}/canais/${canalId}/mensagens`, { texto, respondeAId }),
  /** Corrige o texto de uma mensagem minha; o fio mostra que foi editada. */
  editar: (canalId: string, mensagemId: string, texto: string) =>
    api.patch<MensagemCanalChat>(`${base}/canais/${canalId}/mensagens/${mensagemId}`, { texto }),
  apagar: (canalId: string, mensagemId: string) =>
    api.delete<MensagemCanalChat>(`${base}/canais/${canalId}/mensagens/${mensagemId}`),
  marcarLido: (canalId: string) => api.post<void>(`${base}/canais/${canalId}/lido`),

  /** Prende a mensagem no alto da conversa, com prazo. */
  fixarMensagem: (canalId: string, mensagemId: string, prazo: PrazoDeFixacao) =>
    api.post<void>(`${base}/canais/${canalId}/mensagens/${mensagemId}/fixar`, { prazo }),

  desafixarMensagem: (canalId: string, mensagemId: string) =>
    api.delete<void>(`${base}/canais/${canalId}/mensagens/${mensagemId}/fixar`),

  /** Favoritar a conversa: fica dourada e entra no filtro "Favoritos". */
  favoritarCanal: (canalId: string, favorito: boolean) =>
    api.post<void>(`${base}/canais/${canalId}/favoritar`, { favorito }),

  arquivar: (canalId: string, arquivado: boolean) =>
    api.post<void>(`${base}/canais/${canalId}/arquivar`, { arquivado }),

  /** Anexo em dois passos: pede a URL, manda o arquivo direto ao storage e confirma. */
  async enviarArquivo(canalId: string, arquivo: File, legenda?: string) {
    const destino = await api.post<DestinoUpload>(`${base}/canais/${canalId}/anexos/preparar`, {
      nome: arquivo.name,
      mimeType: arquivo.type,
      tamanho: arquivo.size,
    });
    const envio = await fetch(destino.url, {
      method: 'PUT',
      body: arquivo,
      headers: { 'Content-Type': arquivo.type },
    });
    if (!envio.ok) throw new Error('Não consegui enviar o arquivo. Tente de novo.');
    return api.post<MensagemCanalChat>(`${base}/canais/${canalId}/anexos`, {
      chave: destino.chave,
      nome: arquivo.name,
      mimeType: arquivo.type,
      legenda: legenda ?? '',
    });
  },

  /** O aviso no celular para as conversas internas — pela conta, não por atendimento. */
  inscreverPush: (inscricao: InscricaoPushEntrada) =>
    api.post<void>(`${base}/push/inscrever`, inscricao),
};
